import type { Momento, ResultadoImgq } from "@caint/engine";
import { useMemo, useState } from "react";
import { engine } from "../../lib/engine.ts";
import type { ItemMerged } from "../../state/useAppState.ts";

/**
 * Por que mexer no SISTEMA (pesos, coeficientes, QACI, IMS) não sobe o IMGQ — só
 * subir MATURIDADE sobe. Demonstra ao vivo, sobre os dados reais do ciclo, os dois
 * mecanismos que "prendem" o resultado (motor `packages/engine/src/imgq.ts`):
 *
 *  1. VETO de exclusão: `efetivo = min(bruto, 0,599)` sempre que há CORE em nível
 *     0/1 OU algum eixo < 46%. Enquanto trava, melhorar o bruto (pesos/curva) é
 *     absorvido pelo `min`.
 *  2. TETOS de faixa no ajuste final: QACI/IMS movem o número só DENTRO da faixa
 *     do efetivo — nunca promovem de selo.
 *
 * O único jeito de subir de faixa é destravar (zerar CORE 0/1 e levar todo eixo a
 * ≥ 46%), o que só a maturidade faz. O QACI/IMS aqui é uma HIPÓTESE de avaliação
 * final sobre a maturidade atual (copiada para `mf`), para isolar o efeito do ajuste.
 */
export function SimuladorSensibilidade({
  items,
  imgq,
}: {
  items: ItemMerged[];
  momento: Momento;
  imgq: ResultadoImgq;
}) {
  const [qaci, setQaci] = useState(1); // começa no máximo, para dramatizar o teto
  const [ims, setIms] = useState(1);

  // Hipótese de avaliação final: usa a maturidade atual como `mf` e aplica o ajuste.
  const itensMf = useMemo(() => items.map((it) => ({ ...it, mf: it.mi ?? it.mf ?? null })), [items]);
  const ajuste = useMemo(() => engine.imgq(itensMf, "mf", qaci, ims), [itensMf, qaci, ims]);

  const efetivo = imgq.efetivo;
  const ajustado = ajuste.ajustado;
  const ganho = ajustado - efetivo;
  // Limiar de selo imediatamente acima do efetivo (o teto que o ajuste não cruza).
  const proximoSelo =
    efetivo < 0.46
      ? { v: 0.46, nome: "Bronze" }
      : efetivo < 0.6
        ? { v: 0.6, nome: "Prata" }
        : efetivo < 0.85
          ? { v: 0.85, nome: "Ouro" }
          : null;
  const seloEfetivo = engine.selo(efetivo);
  const seloAjustado = engine.selo(ajustado);
  const mudouSelo = seloEfetivo.chave !== seloAjustado.chave;

  return (
    <section className="m-card pad rel-sec">
      <div className="rel-sec-head">
        <h3>Por que mexer no sistema não sobe o IMGQ — só a maturidade sobe</h3>
        <span className="spec">demonstração com os dados reais</span>
      </div>

      <p className="spec" style={{ lineHeight: 1.5 }}>
        O IMGQ tem dois <b>freios de projeto</b> que protegem o significado do selo. Mexer em pesos,
        coeficientes ou monitoramento esbarra neles; só a <b>maturidade dos itens</b> os solta.
      </p>

      {/* Freio 1 — veto de exclusão */}
      <h4 className="rel-h4">1 · Veto de exclusão — o teto de 59,9%</h4>
      <div className="rel-stats">
        <div className="rel-stat">
          <span className="l">IMGQ bruto</span>
          <span className="v">{engine.pct(imgq.bruto)}</span>
          <span className="f">média ponderada dos eixos</span>
        </div>
        <div className="rel-stat">
          <span className="l">IMGQ efetivo</span>
          <span className="v" style={{ color: imgq.travado ? "var(--danger)" : "var(--success)" }}>
            {engine.pct(efetivo)}
          </span>
          <span className="f">{imgq.travado ? "min(bruto, 59,9%)" : "sem trava"}</span>
        </div>
        <div className="rel-stat">
          <span className="l">CORE em 0/1</span>
          <span className="v" style={{ color: imgq.core0 + imgq.core1 > 0 ? "var(--danger)" : undefined }}>
            {imgq.core0 + imgq.core1}
          </span>
          <span className="f">gatilho do veto</span>
        </div>
      </div>
      <p className="spec rel-nota">
        {imgq.travado ? (
          <>
            Este ciclo está <b>travado</b>: o efetivo é <code>min(bruto, 59,9%)</code>. Enquanto travar,
            aumentar o bruto (mudando pesos por eixo, a curva de score ou a ponderação) é{" "}
            <b>absorvido pelo mínimo</b> — o efetivo não passa de 59,9%. Reordenar pesos só redistribui o que
            já está abaixo do teto.
          </>
        ) : (
          <>
            Este ciclo <b>não</b> está travado (sem CORE em 0/1 e todos os eixos ≥ 46%). Aqui os pesos voltam
            a importar — mas note que chegar até aqui exigiu a maturidade, não a configuração.
          </>
        )}
      </p>

      {/* Freio 2 — tetos de faixa no ajuste final (QACI/IMS) */}
      <h4 className="rel-h4">2 · Ajuste final (QACI/IMS) — preso à faixa do efetivo</h4>
      <p className="spec">
        Hipótese: se esta maturidade fosse a <b>avaliação final</b>, com QACI e IMS no valor abaixo. Arraste —
        o ajustado se move <b>dentro da faixa</b>, mas não cruza o próximo selo.
      </p>
      <div style={{ display: "grid", gap: 8, margin: "8px 0" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 46, fontSize: 12, fontWeight: 700 }}>QACI</span>
          <input
            type="range"
            min={-1}
            max={1}
            step={0.05}
            value={qaci}
            onChange={(e) => setQaci(Number(e.target.value))}
            style={{ flex: 1 }}
          />
          <b style={{ fontFamily: "var(--font-mono)", minWidth: 44, textAlign: "right" }}>
            {engine.num2(qaci)}
          </b>
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 46, fontSize: 12, fontWeight: 700 }}>IMS</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={ims}
            onChange={(e) => setIms(Number(e.target.value))}
            style={{ flex: 1 }}
          />
          <b style={{ fontFamily: "var(--font-mono)", minWidth: 44, textAlign: "right" }}>
            {engine.num2(ims)}
          </b>
        </label>
      </div>
      <div className="rel-stats">
        <div className="rel-stat">
          <span className="l">Efetivo (base)</span>
          <span className="v">{engine.pct(efetivo)}</span>
          <span className="f">selo {seloEfetivo.rotulo || "—"}</span>
        </div>
        <div className="rel-stat">
          <span className="l">Ajustado</span>
          <span className="v" style={{ color: mudouSelo ? "var(--success)" : undefined }}>
            {engine.pct(ajustado)}
          </span>
          <span className="f">
            {ganho >= 0 ? "+" : ""}
            {engine.pct(ganho)} · selo {seloAjustado.rotulo || "—"}
          </span>
        </div>
        {proximoSelo ? (
          <div className="rel-stat">
            <span className="l">Teto da faixa</span>
            <span className="v">{engine.pct(proximoSelo.v - 0.001)}</span>
            <span className="f">não cruza p/ {proximoSelo.nome}</span>
          </div>
        ) : null}
      </div>
      <p className="spec rel-nota">
        {mudouSelo ? (
          <>
            O selo mudou — porque o efetivo já estava na borda; ainda assim o ajuste respeita o teto da faixa.
          </>
        ) : proximoSelo ? (
          <>
            Mesmo com QACI e IMS no máximo, o ajustado <b>não alcança {engine.pct(proximoSelo.v)}</b> ({" "}
            {proximoSelo.nome}). O ajuste é um <b>refinamento dentro da faixa</b>, nunca uma promoção de selo.
          </>
        ) : (
          <>Selo Ouro: aqui o ajuste soma livremente, sem teto acima.</>
        )}
      </p>

      {/* Conclusão */}
      <div className="rel-veredito" style={{ borderLeftColor: "var(--primary)", marginTop: 14 }}>
        <b>Conclusão.</b> Pesos, coeficientes, QACI e IMS mexem só <b>dentro da faixa</b> em que a maturidade
        já colocou a unidade. Para <b>mudar de selo</b> é preciso <b>destravar</b>: zerar os itens CORE em
        nível 0/1 e levar todos os eixos a ≥ 46% — e isso só a maturidade dos itens faz. O caminho legítimo (e
        mais curto) está no <b>plano de menor esforço</b> acima.
      </div>
    </section>
  );
}
